import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  REPORT_MAX_RANGE_DAYS,
  REPORT_PRESETS,
  ReportPreset,
  SalesProductRanking,
  type SalesReportQuery,
} from '@optiflow/types';
import { reportAreaVisible } from '@optiflow/shared';
import { ReportArea } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { PageHeader } from '@/components/ui/PageHeader';
import { apiErrorCode } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import { useSalesReportQuery } from '../api/reports.api';
import { ReportFilterBar, type ReportFilters } from '../components/ReportFilterBar';
import { ReportMetaLine } from '../components/ReportMetaLine';
import { ReportTabs } from '../components/ReportTabs';
import { SalesContent, SalesSkeleton } from '../components/SalesContent';
import { SalesFilterBar } from '../components/SalesFilterBar';

/** The filters this page keeps in the URL, so a reading can be shared or reloaded unchanged. */
function readFilters(params: URLSearchParams) {
  const preset = params.get('preset');
  return {
    period: {
      preset: (REPORT_PRESETS.includes(preset as ReportPreset)
        ? preset
        : ReportPreset.THIS_MONTH) as ReportPreset,
      from: params.get('from') ?? '',
      to: params.get('to') ?? '',
      branchId: params.get('branch') ?? '',
      compare: (params.get('compare') === 'none' ? 'none' : 'previous') as 'previous' | 'none',
    } satisfies ReportFilters,
    operatorId: params.get('operator') ?? '',
    productId: params.get('product') ?? '',
    variantId: params.get('variant') ?? '',
    categoryId: params.get('category') ?? '',
    brandId: params.get('brand') ?? '',
    ranking: (params.get('ranking') === SalesProductRanking.QUANTITY
      ? SalesProductRanking.QUANTITY
      : SalesProductRanking.NET_SALES) as SalesProductRanking,
  };
}

/**
 * Reports › Sales.
 *
 * The five figures at the top are the Overview's own, computed by the same server-side layer; this
 * page adds the breakdowns. Filters are ids in the URL and are resolved — and refused — server-side.
 */
export function SalesReportPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const { period } = filters;

  // Visibility only: the server refuses the same request anyway. Asking for a report the reader
  // cannot have would simply render a 403 where an explanation belongs.
  const grants = useAuthStore((state) => state.user?.permissions);
  const allowed = reportAreaVisible(grants, ReportArea.SALES);

  const customReady =
    period.preset !== ReportPreset.CUSTOM || (period.from !== '' && period.to !== '');

  const query: SalesReportQuery = {
    preset: period.preset,
    ...(period.preset === ReportPreset.CUSTOM ? { from: period.from, to: period.to } : {}),
    branchId: period.branchId || undefined,
    compare: period.compare,
    operatorId: filters.operatorId || undefined,
    productId: filters.productId || undefined,
    variantId: filters.variantId || undefined,
    categoryId: filters.categoryId || undefined,
    brandId: filters.brandId || undefined,
    ranking: filters.ranking,
  };
  const report = useSalesReportQuery(query, { enabled: customReady && allowed });

  const write = (next: Partial<ReturnType<typeof readFilters>>) => {
    const merged = { ...filters, ...next, period: { ...period, ...(next.period ?? {}) } };
    const search = new URLSearchParams();
    search.set('preset', merged.period.preset);
    if (merged.period.preset === ReportPreset.CUSTOM) {
      if (merged.period.from) search.set('from', merged.period.from);
      if (merged.period.to) search.set('to', merged.period.to);
    }
    if (merged.period.branchId) search.set('branch', merged.period.branchId);
    if (merged.period.compare === 'none') search.set('compare', 'none');
    for (const [key, value] of [
      ['operator', merged.operatorId],
      ['product', merged.productId],
      ['variant', merged.variantId],
      ['category', merged.categoryId],
      ['brand', merged.brandId],
    ] as const) {
      if (value) search.set(key, value);
    }
    if (merged.ranking === SalesProductRanking.QUANTITY) search.set('ranking', merged.ranking);
    setParams(search, { replace: true });
  };

  return (
    <div className="space-y-5">
      <PageHeader title={t('reports.sales.title')} subtitle={t('reports.sales.subtitle')} />
      <ReportTabs />
      {allowed ? null : <Alert tone="info">{t('reports.sales.noAccess')}</Alert>}
      {allowed ? (
        <ReportFilterBar value={period} onChange={(next) => write({ period: next })} />
      ) : null}
      {allowed ? (
        <SalesFilterBar
          value={{
            operatorId: filters.operatorId,
            categoryId: filters.categoryId,
            brandId: filters.brandId,
            ranking: filters.ranking,
          }}
          applied={report.data?.filters ?? null}
          operators={report.data?.byOperator ?? []}
          categories={report.data?.byCategory ?? []}
          brands={report.data?.byBrand ?? []}
          onChange={(next) => write(next)}
        />
      ) : null}
      {report.data ? <ReportMetaLine meta={report.data.meta} /> : null}
      {!customReady ? <Alert tone="info">{t('reports.filters.chooseDates')}</Alert> : null}
      {report.isError ? (
        <Alert tone="error">
          {apiErrorCode(report.error) === 'VALIDATION_FAILED'
            ? t('reports.invalidPeriod', { days: REPORT_MAX_RANGE_DAYS })
            : t('reports.loadError')}
        </Alert>
      ) : null}
      {report.isPending && customReady && allowed ? <SalesSkeleton /> : null}
      {report.data ? <SalesContent report={report.data} query={query} /> : null}
    </div>
  );
}
